/**
 * SLA Service API Client
 * Feature: 024-sla-tier-configuration
 *
 * Frontend API client for SLA configuration endpoints.
 * 
 * Refactored: 2026-01-15 (Spec 101 T083, T086)
 * - Converted from raw fetch() to centralized apiClient (axios)
 * - Consistent error handling with other services
 * - Added Zod runtime response validation
 */

import { apiClient } from './apiClient';
import { z } from 'zod';
import {
  slaTierSchema,
  clientSlaAssignmentSchema,
  clientSlaAssignmentWithTierSchema,
  clientSlaOverrideSchema,
  maintenanceWindowSchema,
  effectiveSlaSchema,
  validateResponse,
} from './validation';
import {
  SLATier,
  CreateSLATierRequest,
  UpdateSLATierRequest,
  SLATierFilters,
  ClientSLAAssignment,
  ClientSLAAssignmentWithTier,
  AssignSLATierRequest,
  ClientSLAOverride,
  CreateOverrideRequest,
  UpdateOverrideRequest,
  MaintenanceWindow,
  CreateMaintenanceWindowRequest,
  UpdateMaintenanceWindowRequest,
  EffectiveSLA,
} from '@shared/types/sla';

const API_BASE = '/api/sla';

// =============================================================================
// SLA Tiers API
// =============================================================================

export async function getTiers(filters: SLATierFilters = {}): Promise<SLATier[]> {
  const params = new URLSearchParams();
  if (filters.include_deprecated) params.append('include_deprecated', 'true');

  const response = await apiClient.get<{ success: boolean; data: SLATier[] }>(
    `${API_BASE}/tiers?${params}`
  );
  return validateResponse(z.array(slaTierSchema), response.data.data, 'getTiers');
}

export async function getTier(id: string): Promise<SLATier> {
  const response = await apiClient.get<{ success: boolean; data: SLATier }>(
    `${API_BASE}/tiers/${id}`
  );
  return validateResponse(slaTierSchema, response.data.data, 'getTier');
}

export async function createTier(data: CreateSLATierRequest): Promise<SLATier> {
  const response = await apiClient.post<{ success: boolean; data: SLATier }>(
    `${API_BASE}/tiers`,
    data
  );
  return validateResponse(slaTierSchema, response.data.data, 'createTier');
}

export async function updateTier(id: string, data: UpdateSLATierRequest): Promise<SLATier> {
  const response = await apiClient.put<{ success: boolean; data: SLATier }>(
    `${API_BASE}/tiers/${id}`,
    data
  );
  return validateResponse(slaTierSchema, response.data.data, 'updateTier');
}

export async function deleteTier(id: string, force: boolean = false): Promise<void> {
  const params = force ? '?force=true' : '';
  await apiClient.delete(`${API_BASE}/tiers/${id}${params}`);
}

export async function getAffectedClients(tierId: string): Promise<any[]> {
  const response = await apiClient.get<{ success: boolean; data: any[] }>(
    `${API_BASE}/tiers/${tierId}/affected-clients`
  );
  return response.data.data;
}

// =============================================================================
// Client Assignments API (Phase 4)
// =============================================================================

export async function getAssignments(clientId?: string): Promise<ClientSLAAssignmentWithTier[]> {
  const params = clientId ? `?client_id=${clientId}` : '';
  const response = await apiClient.get<{ success: boolean; data: ClientSLAAssignmentWithTier[] }>(
    `${API_BASE}/assignments${params}`
  );
  return validateResponse(z.array(clientSlaAssignmentWithTierSchema), response.data.data, 'getAssignments');
}

export async function getCurrentAssignment(
  clientId: string
): Promise<ClientSLAAssignmentWithTier | null> {
  const response = await apiClient.get<{ success: boolean; data: ClientSLAAssignmentWithTier | null }>(
    `${API_BASE}/assignments/client/${clientId}/current`
  );
  return response.data.data ? validateResponse(clientSlaAssignmentWithTierSchema, response.data.data, 'getCurrentAssignment') : null;
}

export async function getAssignmentHistory(
  clientId: string
): Promise<ClientSLAAssignmentWithTier[]> {
  const response = await apiClient.get<{ success: boolean; data: ClientSLAAssignmentWithTier[] }>(
    `${API_BASE}/assignments/client/${clientId}/history`
  );
  return validateResponse(z.array(clientSlaAssignmentWithTierSchema), response.data.data, 'getAssignmentHistory');
}

export async function createAssignment(data: AssignSLATierRequest): Promise<ClientSLAAssignment> {
  const response = await apiClient.post<{ success: boolean; data: ClientSLAAssignment }>(
    `${API_BASE}/assignments`,
    data
  );
  return validateResponse(clientSlaAssignmentSchema, response.data.data, 'createAssignment');
}

// Alias for backward compatibility
export const assignTier = createAssignment;

// =============================================================================
// Client Overrides API (Phase 5)
// =============================================================================

export async function getOverrides(clientId?: string): Promise<ClientSLAOverride[]> {
  const params = clientId ? `?client_id=${clientId}` : '';
  const response = await apiClient.get<{ success: boolean; data: ClientSLAOverride[] }>(
    `${API_BASE}/overrides${params}`
  );
  return validateResponse(z.array(clientSlaOverrideSchema), response.data.data, 'getOverrides');
}

export async function createOverride(data: CreateOverrideRequest): Promise<ClientSLAOverride> {
  const response = await apiClient.post<{ success: boolean; data: ClientSLAOverride }>(
    `${API_BASE}/overrides`,
    data
  );
  return validateResponse(clientSlaOverrideSchema, response.data.data, 'createOverride');
}

export async function updateOverride(
  id: string,
  data: UpdateOverrideRequest
): Promise<ClientSLAOverride> {
  const response = await apiClient.put<{ success: boolean; data: ClientSLAOverride }>(
    `${API_BASE}/overrides/${id}`,
    data
  );
  return validateResponse(clientSlaOverrideSchema, response.data.data, 'updateOverride');
}

export async function deleteOverride(id: string): Promise<void> {
  await apiClient.delete(`${API_BASE}/overrides/${id}`);
}

// =============================================================================
// Maintenance Windows API (Phase 6)
// =============================================================================

export async function getMaintenanceWindows(clientId?: string): Promise<MaintenanceWindow[]> {
  const params = clientId ? `?client_id=${clientId}` : '';
  const response = await apiClient.get<{ success: boolean; data: MaintenanceWindow[] }>(
    `${API_BASE}/maintenance-windows${params}`
  );
  return validateResponse(z.array(maintenanceWindowSchema), response.data.data, 'getMaintenanceWindows');
}

export async function createMaintenanceWindow(
  data: CreateMaintenanceWindowRequest
): Promise<MaintenanceWindow> {
  const response = await apiClient.post<{ success: boolean; data: MaintenanceWindow }>(
    `${API_BASE}/maintenance-windows`,
    data
  );
  return validateResponse(maintenanceWindowSchema, response.data.data, 'createMaintenanceWindow');
}

export async function updateMaintenanceWindow(
  id: string,
  data: UpdateMaintenanceWindowRequest
): Promise<MaintenanceWindow> {
  const response = await apiClient.put<{ success: boolean; data: MaintenanceWindow }>(
    `${API_BASE}/maintenance-windows/${id}`,
    data
  );
  return validateResponse(maintenanceWindowSchema, response.data.data, 'updateMaintenanceWindow');
}

export async function deleteMaintenanceWindow(id: string): Promise<void> {
  await apiClient.delete(`${API_BASE}/maintenance-windows/${id}`);
}

// =============================================================================
// Effective SLA API (Phase 7)
// =============================================================================

export async function getEffectiveSLAForClient(clientId: string): Promise<EffectiveSLA> {
  const response = await apiClient.get<{ success: boolean; data: EffectiveSLA }>(
    `${API_BASE}/effective/client/${clientId}`
  );
  return validateResponse(effectiveSlaSchema, response.data.data, 'getEffectiveSLAForClient');
}

export async function getEffectiveSLAForCapacity(capacityId: string): Promise<EffectiveSLA> {
  const response = await apiClient.get<{ success: boolean; data: EffectiveSLA }>(
    `${API_BASE}/effective/capacity/${capacityId}`
  );
  return validateResponse(effectiveSlaSchema, response.data.data, 'getEffectiveSLAForCapacity');
}

export async function getEffectiveSLAForWorkspace(workspaceId: string): Promise<EffectiveSLA> {
  const response = await apiClient.get<{ success: boolean; data: EffectiveSLA }>(
    `${API_BASE}/effective/workspace/${workspaceId}`
  );
  return validateResponse(effectiveSlaSchema, response.data.data, 'getEffectiveSLAForWorkspace');
}

// Default export with all functions
export default {
  getTiers,
  getTier,
  createTier,
  updateTier,
  deleteTier,
  getAffectedClients,
  getAssignments,
  getCurrentAssignment,
  getAssignmentHistory,
  createAssignment,
  assignTier,
  getOverrides,
  createOverride,
  updateOverride,
  deleteOverride,
  getMaintenanceWindows,
  createMaintenanceWindow,
  updateMaintenanceWindow,
  deleteMaintenanceWindow,
  getEffectiveSLAForClient,
  getEffectiveSLAForCapacity,
  getEffectiveSLAForWorkspace,
};
